import { BaseDomainModel } from '../models/base/base-domain-model';
import { inject, Injectable } from '@angular/core';
import { SubmissionsAPI } from '../api/submissions-api';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { ODataQueryOptions } from '../models/shared/odata-query-options';
import { ODataResponse } from '../models/protocols/odata-response';
import { ChangeNameRequest } from '../models/users/requests/submissionRequests/change-name-request';
import { CreateAttachmentRequest } from '../models/users/requests/create-attachment-request';
import { Asset } from '../models/asset/dto/asset';
import { SubmissionAttachment } from '../models/submissions/submission-attachment';
import { catchError, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { MemberSubmission, SubmissionStakeholder } from '../models/submissions/member-submission';
import { TypeService } from '../services/type/type-service';
import { SubmissionStatus } from '../models/shared/submission-status';
import { SubmissionCreated } from '../models/submissions/submission-created';
import { AttachmentFile } from '../models/users/dto/attachment-file';
import { AttachmentApi } from '../api/attachment-api';
import { SubmissionCommentRequest } from '../models/users/requests/submission-comment-request';
import { SubmissionComment } from '../models/submissions/submission-comment';
import { SubmissionStatusCommentRequest } from '../models/submissions/requests/submission-comment-request';
import { SubmissionSubStatusEnum } from '../models/enum/shared/submission-sub-status.enum';
import { UpdateSubmissionOwnerRequest } from '../models/users/requests/update-submission-owner-request';
import { UpdateSubmissionApproverRequest } from '../models/users/requests/update-submission-approver-request';
import { SubmissionRoleTemplate } from '../models/submissions/submission-role-template';
import { MemberSubmissionType } from '../models/submissions/member-submission-type';
import { SubmissionPermissionType } from '../models/permissions/dto/submission-permission-type';
import { SubmissionPermissionWithType } from '../models/permissions/dto/submission-permission-with-type';
import { ChangeAddressRequest } from '../models/users/requests/submissionRequests/change-address-request';
import { SubmissionReminderRequest } from '../models/users/requests/submission-reminder-request';
import { HttpResponse } from '@angular/common/http';
import { PortalService } from '../services/portal/portal.service';
import { UpdatePhoneNumberRequest } from '../models/users/requests/submissionRequests/update-phone-number-request';
import { SubmissionStatusPill } from '../models/shared/submission-status-pill';
import { ChangePersonalDetailsRequest } from '../models/users/requests/submissionRequests/change-personal-details-request';
import { ChangeMartialStatusRequest } from '../models/users/requests/submissionRequests/change-martial-status-request';
import { DropDownItem } from '../models/shared/stylesheet/drop-down-item';
import { PortalType } from '../models/enum/shared/portal-type';
import { IPChangeInvestmentInstructionsSubmissionRequest } from '../models/users/requests/ip-change-investment-instructions-submission-request';
import { MpChangeInvestmentInstructionSubmissionRequest } from '../models/users/requests/submissionRequests/mp-change-investment-instruction-submission-request';
import { CreateVariableBenefitConfigurationSubmissionRequest } from '../models/users/requests/submissionRequests/create-variable-benefit-configuration-submission-request';
import { UpdateVariableBenefitConfigurationRequest } from '../models/users/requests/submissionRequests/update-variable-benefit-configuration-request';
import { IpEmployerSubmission } from '../models/submissions/employer-submissions/ip-employer-submission';
import { OdataUtils } from '../utils/odata-utils';
import { AccountTypeIdEnum } from '../models/transactions/enum/account-type-id.enum';

// Provided by Logged In Scope
@Injectable()
export class SubmissionsDomainModel extends BaseDomainModel {
  constructor(private submissionsApi: SubmissionsAPI, private attachmentApi: AttachmentApi) {
    super();
    this.init();
  }

  private typeService = inject(TypeService);
  private portalService = inject(PortalService);

  private _allMemberSubmissions = new BehaviorSubject<MemberSubmission[] | null>(null);
  public allMemberSubmissions$ = this._allMemberSubmissions as Observable<MemberSubmission[]>;

  private _submissionStatuses = new BehaviorSubject<SubmissionStatus[]>([]);
  public submissionStatuses$ = this._submissionStatuses as Observable<SubmissionStatus[]>;

  private _submissionRoleTemplates = new BehaviorSubject<SubmissionRoleTemplate[]>([]);
  public submissionRoleTemplates$ = this._submissionRoleTemplates as Observable<SubmissionRoleTemplate[]>;

  private _submissionTypes = new BehaviorSubject<MemberSubmissionType[]>([]);
  public submissionTypes$ = this._submissionTypes as Observable<MemberSubmissionType[]>;

  private _submissionPermissionTypes = new BehaviorSubject<SubmissionPermissionType[]>([]);
  public submissionPermissionTypes$ = this._submissionPermissionTypes as Observable<SubmissionPermissionType[]>;

  public submissionStatusPills$: Observable<SubmissionStatusPill[]> = this.submissionStatuses$.pipe(
    map(ss => {
      return ss.flatMap(s => {
        if (s?.subStatuses?.length > 0) {
          return s?.subStatuses.map(st => {
            return new SubmissionStatusPill(st.id, st.name, st.parentStatusId, st.parentStatus);
          });
        }
        return []; // Return an empty array if there are no sub statuses
      });
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public readonly submissionTypeDropdowns$ = this.submissionTypes$.pipe(
    map(types => {
      return types.map(t => new DropDownItem(t.name, t.id.toString()));
    })
  );

  private _allMemberSubmissionsTotalCount = new BehaviorSubject<number | undefined>(undefined);
  public allMemberSubmissionsTotalCount$ = this._allMemberSubmissionsTotalCount as Observable<number | undefined>;

  public init(): void {
    this.fetchSubmissionStatuses();
    this.portalService.isInternal$.once(isInternal => {
      if (isInternal) {
        this.getSubmissionRoleTemplates();
        this.getMemberSubmissionTypes();
        this.getSubmissionPermissionTypes();
      }
    });
  }

  public getMemberSubmissions(oDataQueryOptions: ODataQueryOptions): Observable<ODataResponse<MemberSubmission>> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.getMemberSubmissions(oDataQueryOptions);
          case PortalType.Internal:
            return this.submissionsApi.ipGetMemberSubmissions(oDataQueryOptions);
        }
      }),
      tap(odataRes => this._allMemberSubmissionsTotalCount.next(odataRes['@odata.count'])),
      tap(odataRes => this._allMemberSubmissions.next(odataRes.value)),
      catchError(err => {
        this._allMemberSubmissions.next([]);
        this._allMemberSubmissionsTotalCount.next(0);
        return throwError(() => err);
      })
    );
  }

  public getRecentMemberSubmissions(memberId: number): Observable<MemberSubmission[]> {
    const queryOptions = new ODataQueryOptions();
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    const isoDate = thirtyDaysAgo.toISOString();
    queryOptions.setFilter(`memberId eq ${memberId} and createdDate ge ${isoDate}`);
    return this.submissionsApi.ipGetMemberSubmissions(queryOptions).pipe(
      map(res => res.value),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getRecentEmployerSubmissions(employerId: number): Observable<IpEmployerSubmission[]> {
    const queryOptions = new ODataQueryOptions();
    const now = new Date();
    const thirtyDaysAgo = new Date(now);
    thirtyDaysAgo.setDate(now.getDate() - 30);
    const isoDate = thirtyDaysAgo.toISOString();
    queryOptions.setFilter(`employerId eq ${employerId} and createdDate ge ${isoDate}`);
    return this.submissionsApi.ipGetEmployerSubmissions(queryOptions).pipe(
      map(res => res.value),
      catchError(err => {
        return throwError(() => err);
      })
    );
  }

  public getMemberSubmissionById(id: string): Observable<MemberSubmission> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.getMemberSubmissionById(id);
          case PortalType.Internal:
            return this.submissionsApi.ipGetMemberSubmissionById(id);
        }
      })
    );
  }

  public getSubmissionViewersForSubmissionTypeId(subTypeId: string): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi
      .ipGetMemberSubmissionViewersForMemberSubmissionType(subTypeId)
      .pipe(map(res => res.value));
  }

  public ipGetMemberActiveVariableBenefitConfigSubmission(
    memberId: number,
    opts = new ODataQueryOptions()
  ): Observable<MemberSubmission | null> {
    return this.submissionsApi.ipGetMemberActiveVariableBenefitConfigSubmission(memberId, opts);
  }

  public ipSaveVariableBenefitSubmission(
    req: CreateVariableBenefitConfigurationSubmissionRequest
  ): Observable<MemberSubmission> {
    return this.submissionsApi.ipSaveVariableBenefitSubmission(req);
  }

  public ipSubmitVariableBenefitSubmission(
    req: CreateVariableBenefitConfigurationSubmissionRequest
  ): Observable<MemberSubmission> {
    return this.submissionsApi.ipSubmitVariableBenefitSubmission(req);
  }

  public createMemberNameChangeSubmission(req: ChangeNameRequest): Observable<SubmissionCreated> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.createNameChangeSubmission(req);
          case PortalType.Internal:
            return this.submissionsApi.createInternalNameChangeSubmission(req);
        }
      })
    );
  }

  public createMemberPersonalDetailsChangeSubmission(req: ChangePersonalDetailsRequest): Observable<SubmissionCreated> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.createPersonalDetailsChangeSubmission(req);
          case PortalType.Internal:
            return this.submissionsApi.createInternalPersonalDetailsChangeSubmission(req);
        }
      })
    );
  }

  public createMemberAddressChangeSubmission(req: ChangeAddressRequest): Observable<SubmissionCreated> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.createAddressChangeSubmission(req);
          case PortalType.Internal:
            return this.submissionsApi.createInternalAddressChangeSubmission(req);
        }
      })
    );
  }

  public createUpdatePhoneNumberSubmission(req: UpdatePhoneNumberRequest): Observable<SubmissionCreated> {
    return this.portalService.portalType$.pipe(
      take(1),
      switchMap(portalType => {
        switch (portalType) {
          case PortalType.Member:
          case PortalType.Employer:
            return this.submissionsApi.createUpdatePhoneNumberSubmission(req);
          case PortalType.Internal:
            return this.submissionsApi.createInternalUpdatePhoneNumberSubmission(req);
        }
      })
    );
  }

  public createMemberMaritalStatusSubmission(req: ChangeMartialStatusRequest): Observable<SubmissionCreated> {
    return of(new SubmissionCreated());
  }

  public ipCreateMemberSubmissionAttachment(
    req: CreateAttachmentRequest,
    submissionId: string
  ): Observable<SubmissionAttachment> {
    return this.submissionsApi.ipCreateMemberSubmissionAttachment(req, submissionId);
  }

  public ipAddMemberSubmissionComment(req: SubmissionCommentRequest): Observable<SubmissionComment[]> {
    return this.submissionsApi.ipAddMemberSubmissionComment(req);
  }

  public ipGetMemberSubmissionAttachments(submissionId: string): Observable<SubmissionAttachment[]> {
    return this.attachmentApi.ipGetMemberSubmissionAttachments(submissionId);
  }

  public uploadAttachmentToS3(file: AttachmentFile): Observable<Asset> {
    return this.attachmentApi.uploadAttachmentFile(file);
  }

  public ipDeleteMemberSubmissionAttachment(attachmentId: string, submissionId: string): Observable<any> {
    return this.attachmentApi.ipDeleteMemberSubmissionAttachment(attachmentId, submissionId);
  }

  public updateMemberSubmissionStatusWithComment(req: SubmissionStatusCommentRequest, url: string): Observable<any> {
    return this.submissionsApi.updateMemberSubmissionStatusWithComment(req, url);
  }

  public ipUpdateMemberSubmissionStatus(status: SubmissionSubStatusEnum, submissionId: string): Observable<any> {
    return this.submissionsApi.ipUpdateMemberSubmissionStatus(status, submissionId);
  }

  private fetchSubmissionStatuses(): void {
    this.typeService.submissionStatuses$.once(ss => this._submissionStatuses.next(ss));
  }

  public getFilteredMemberSubmissionOwners(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeFilterValue(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeFilterValue(filterString)}')`
    );
    return this.submissionsApi.ipGetMemberSubmissionOwnersForSubmissionType(queryOptions).pipe(map(res => res.value));
  }

  public ipGetMemberSubmissionOwnersForSubmissionTypeId(
    submissionTypeId: string,
    opts = new ODataQueryOptions()
  ): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi
      .ipGetMemberSubmissionOwnersForSubmissionType(opts, submissionTypeId)
      .pipe(map(res => res.value));
  }

  public getMemberSubmissionApproversForSubmissionTypeId(
    submissionTypeId: string
  ): Observable<SubmissionStakeholder[]> {
    return this.submissionsApi
      .ipGetMemberSubmissionApproversForSubmissionType(new ODataQueryOptions(), submissionTypeId)
      .pipe(map(res => res.value));
  }

  public getFilteredMemberSubmissionApprovers(filterString: string): Observable<SubmissionStakeholder[]> {
    const queryOptions = new ODataQueryOptions();
    queryOptions.setFilter(
      `contains(firstName, '${OdataUtils.escapeFilterValue(filterString)}')` +
        `or contains(lastName, '${OdataUtils.escapeFilterValue(filterString)}')`
    );
    return this.submissionsApi
      .ipGetMemberSubmissionApproversForSubmissionType(queryOptions)
      .pipe(map(res => res.value));
  }

  public getSubmissionRoleTemplates(): void {
    this.submissionsApi
      .getSubmissionRoleTemplates()
      .pipe(map(res => res.value))
      .once(templates => this._submissionRoleTemplates.next(templates));
  }

  public getSubmissionPermissionTypes(): void {
    this.submissionsApi.getSubmissionPermissionTypes().once(res => {
      const types = res.map(t => {
        switch (t.name) {
          case 'View':
            t.icon = '/assets/icons/grey/SearchCircle.svg';
            t.tooltipText = $localize`Allows user to view submission details`;
            break;
          case 'Approve':
            t.icon = '/assets/icons/grey/CircleCheck.svg';
            t.tooltipText = $localize`Allows user to approve submissions`;
            break;
          case 'Approve Own':
            t.icon = '/assets/icons/grey/CardCheck.svg';
            t.tooltipText = $localize`Allows user to own and approve submission without secondary approval`;
            break;
          case 'Override':
            t.icon = '/assets/icons/grey/Pen.svg';
            t.tooltipText = $localize`Allows user to change the submission owner and/or approver without being
            assigned to the submission themselves`;
            break;
          case 'Process':
            t.icon = '/assets/icons/grey/CardInfo.svg';
            t.tooltipText = $localize`Allows user to be the owner of a submission and makes them available
            in the queue for submission assignment`;
            break;
          case 'Create':
            t.icon = '/assets/icons/grey/CardPlus.svg';
            t.tooltipText = $localize`Allows user to create submissions on the member's behalf `;
            break;
        }
        return t;
      });
      this._submissionPermissionTypes.next(types);
    });
  }

  public getAllSubmissionPermissions(): Observable<SubmissionPermissionWithType[]> {
    return this.submissionsApi.getAllSubmissionPermissions().pipe(
      map(res => res.value),
      shareReplay({
        bufferSize: 1,
        refCount: true
      })
    );
  }

  public getMemberSubmissionTypes(): void {
    this.portalService.isInternal$
      .pipe(
        take(1),
        switchMap(isInternal => {
          if (isInternal) {
            return this.submissionsApi.ipGetMemberSubmissionTypes();
          } else {
            return this.submissionsApi.getMemberSubmissionTypes();
          }
        }),
        map(res => res.value)
      )
      .once(types => this._submissionTypes.next(types));
  }

  public ipUpdateMemberSubmissionOwner(req: UpdateSubmissionOwnerRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberSubmissionOwner(req);
  }

  public ipUpdateMemberSubmissionApprover(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberSubmissionApprover(req);
  }

  public ipSetMemberSubmissionToReadyForApproval(req: UpdateSubmissionApproverRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipSetMemberSubmissionToReadyForApproval(req);
  }

  public ipCreateMemberSubmissionReminder(req: SubmissionReminderRequest): Observable<MemberSubmission> {
    return this.submissionsApi.ipCreateMemberSubmissionReminder(req);
  }

  public ipDeleteMemberSubmissionReminder(submissionId: string, reminderId: string): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipDeleteMemberSubmissionReminder(submissionId, reminderId);
  }

  public ipDeleteVariableBenefitSubmission(submissionId: number): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipDeleteVariableBenefitSubmission(submissionId);
  }

  public ipDeleteVariableBenefitConfigurationConsentWaiver(submissionId: number): Observable<HttpResponse<any>> {
    return this.submissionsApi.ipDeleteVariableBenefitConfigurationConsentWaiver(submissionId);
  }

  public createMemberInvestmentInstructionsSubmission(
    req: MpChangeInvestmentInstructionSubmissionRequest,
    accountTypeId: AccountTypeIdEnum
  ): Observable<SubmissionCreated> {
    return this.submissionsApi.createMemberInvestmentInstructionsSubmission(req, accountTypeId);
  }

  public createIPMemberInvestmentInstructionsSubmission(
    req: IPChangeInvestmentInstructionsSubmissionRequest
  ): Observable<SubmissionCreated> {
    return this.submissionsApi.createIPMemberInvestmentInstructionsSubmission(req);
  }

  public ipUpdateMemberVariableBenefitConfigSubmission(
    req: UpdateVariableBenefitConfigurationRequest
  ): Observable<MemberSubmission> {
    return this.submissionsApi.ipUpdateMemberVariableBenefitConfigSubmission(req);
  }
}
